/**
 * Group invite-link codec.
 *
 * The invite link carries the master key out-of-band so a new member can join
 * without trusting the server: anyone holding the link gets the 32-byte master
 * key and the 16-byte derived public id, recomputes the public id locally, and
 * refuses to proceed if the two halves disagree.
 *
 * Wire format:
 *   kaether:group:invite:v1:<base64url(masterKey[32] || publicId[16])>
 *
 * The 48-byte payload encodes as ~64 base64url characters (no padding).
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/GroupInviteLink.swift` byte-for-byte.
 */

import {
  GroupMasterKey,
  GroupPublicId,
  GroupIdentityError,
  derivePublicId,
  base64UrlEncode,
  base64UrlDecode,
} from "./GroupIdentity";

// ---------------------------------------------------------------------------
// Value object
// ---------------------------------------------------------------------------

/**
 * Decoded invite link: master key + the public id derived from it. Both halves
 * are kept so callers can compare against a server-provided public id without
 * re-deriving.
 */
export interface GroupInviteLink {
  readonly masterKey: GroupMasterKey;
  readonly publicId: GroupPublicId;
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

export const GROUP_INVITE_PREFIX = "kaether:group:invite:v1:";

/** Cheap prefix check — does not validate the payload. */
export function isGroupInviteLink(s: string): boolean {
  return s.startsWith(GROUP_INVITE_PREFIX);
}

/**
 * Encodes `masterKey || derivePublicId(masterKey)` (48 bytes) as base64url
 * with the v1 prefix. Always derives the public id from the master key —
 * never trusts a caller-supplied one, so we cannot produce an
 * internally-inconsistent link.
 */
export async function encodeGroupInviteLink(
  masterKey: GroupMasterKey,
): Promise<string> {
  const publicId = await derivePublicId(masterKey);
  const payload = new Uint8Array(48);
  payload.set(masterKey.raw, 0);
  payload.set(publicId.raw, 32);
  return GROUP_INVITE_PREFIX + base64UrlEncode(payload);
}

/**
 * Parses, splits into the two halves, and verifies that the supplied public id
 * equals `derivePublicId(masterKey)`. Throws
 * `GroupIdentityError("publicIdDoesNotMatchMasterKey")` on mismatch — this is
 * the integrity check that prevents the server (or a MITM) from rewriting one
 * half of the link while keeping the other.
 *
 * Throws `GroupIdentityError("invalidInviteLink")` for malformed inputs.
 */
export async function decodeGroupInviteLink(
  s: string,
): Promise<GroupInviteLink> {
  if (!s.startsWith(GROUP_INVITE_PREFIX)) {
    throw new GroupIdentityError(
      "invalidInviteLink",
      "missing prefix kaether:group:invite:v1:",
    );
  }
  const b64 = s.slice(GROUP_INVITE_PREFIX.length);
  const data = base64UrlDecode(b64);
  if (data === null) {
    throw new GroupIdentityError(
      "invalidInviteLink",
      "invalid base64url payload",
    );
  }
  if (data.length !== 48) {
    throw new GroupIdentityError(
      "invalidInviteLink",
      `expected 48 bytes, got ${data.length}`,
    );
  }

  let masterKey: GroupMasterKey;
  let publicId: GroupPublicId;
  try {
    masterKey = new GroupMasterKey(data.slice(0, 32));
    publicId = new GroupPublicId(data.slice(32, 48));
  } catch (err) {
    throw new GroupIdentityError(
      "invalidInviteLink",
      `length validation: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  // Critical: re-derive public id from master key and reject if they disagree.
  // This blocks link-rewriting attacks where an attacker keeps one half and
  // substitutes the other.
  const derived = await derivePublicId(masterKey);
  if (!derived.equals(publicId)) {
    throw new GroupIdentityError(
      "publicIdDoesNotMatchMasterKey",
      "publicId embedded in link does not match HKDF derivation from masterKey",
    );
  }

  return { masterKey, publicId };
}

/**
 * Convenience: returns null for non-invite-link strings (so URL handlers can
 * probe without throwing). Re-throws for malformed links that *do* claim to be
 * invite links — those should surface as user-visible errors, not silent
 * no-ops.
 */
export async function tryDecodeGroupInviteLink(
  s: string,
): Promise<GroupInviteLink | null> {
  if (!isGroupInviteLink(s)) return null;
  return decodeGroupInviteLink(s);
}
